import { useEffect, useRef, useState } from 'react'
import { post } from './api'

/**
 * Subscribes to the student's pass over SSE.
 *
 * The server sends the pass once on connect and again whenever a booth scans
 * this student, so there is no polling at all. `EventSource` reconnects by
 * itself, which is what makes this safe on venue Wi-Fi.
 *
 * A ticket is fetched first because `EventSource` cannot set an
 * Authorization header, and putting the 24-hour session token in the URL
 * would write it into every access log. The ticket is single-use and lasts a
 * minute.
 */
export function useLivePass<T>(enabled: boolean) {
  const [data, setData] = useState<T | null>(null)
  const [state, setState] = useState<'connecting' | 'live' | 'error'>('connecting')

  /** Survives reconnects so a dropped stream does not blank the screen. */
  const latest = useRef<T | null>(null)

  useEffect(() => {
    if (!enabled) return

    let source: EventSource | null = null
    let retry: number | null = null
    let cancelled = false
    let attempt = 0

    const connect = async () => {
      if (cancelled) return

      try {
        const { ticket } = await post<{ ticket: string }>('/api/student/stream-ticket', {})
        if (cancelled) return

        source = new EventSource(`/api/student/pass/stream?t=${encodeURIComponent(ticket)}`)

        source.addEventListener('pass', (event) => {
          const next = JSON.parse((event as MessageEvent).data) as T
          latest.current = next
          setData(next)
          setState('live')
          attempt = 0
        })

        source.onerror = () => {
          /*
           * Reconnect by hand rather than letting EventSource retry: its
           * automatic retry would reuse the URL, and the ticket in it has
           * already been spent. Each attempt needs a fresh one.
           */
          source?.close()
          source = null
          if (cancelled) return

          setState('error')
          attempt += 1
          const backoff = Math.min(1000 * 2 ** (attempt - 1), 15_000)
          retry = window.setTimeout(connect, backoff)
        }
      } catch {
        if (cancelled) return
        setState('error')
        attempt += 1
        retry = window.setTimeout(connect, Math.min(1000 * 2 ** (attempt - 1), 15_000))
      }
    }

    void connect()

    return () => {
      cancelled = true
      if (retry) window.clearTimeout(retry)
      source?.close()
    }
  }, [enabled])

  return { data: data ?? latest.current, state }
}
